import React, { useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiX, FiExternalLink, FiZap } from 'react-icons/fi';
import { getDoc } from '../../services/docsService';
import { markVersionSeen } from '../../services/versionService';
import { DocsMarkdown } from '../docs/DocsMarkdown';

interface WhatsNewModalProps {
  version: string;
  onDismiss: () => void;
}

export const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ version, onDismiss }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // Block body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape key to dismiss
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onDismiss]);

  const handleOpenFullPage = () => {
    onDismiss();
    // Land on the latest release inside the unified docs surface, with the
    // Release Notes group auto-expanded for easy access to prior releases.
    navigate(`/docs/${version}?group=Release%20Notes`);
  };

  // Fetch via the unified docs API. Marks the version seen on success so
  // the header badge clears naturally — same behavior the prior
  // ChangelogContent provided.
  const docQuery = useQuery(
    ['docs', version],
    () => getDoc(version),
    {
      enabled: !!version,
      onSuccess: () => markVersionSeen(version),
    },
  );

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center p-4 sm:p-6 lg:p-10"
      aria-modal="true"
      role="dialog"
      aria-label="What's New"
    >
      {/* ── Backdrop ── */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onDismiss}
      />

      {/* ── Modal shell ── */}
      <div
        className={`
          relative z-10 w-full max-w-5xl flex flex-col rounded-2xl shadow-2xl overflow-hidden
          animate-scaleIn
          ${darkMode
            ? 'bg-gray-900 ring-1 ring-white/10'
            : 'bg-white ring-1 ring-gray-200'
          }
        `}
        style={{ height: 'min(85vh, 780px)' }}
      >

        {/* ── Decorative animated top bar ── */}
        <div className="relative h-1.5 w-full overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
          <div
            className="absolute inset-0 opacity-60"
            style={{
              backgroundImage: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'whatsNewShimmer 2.5s infinite linear',
            }}
          />
        </div>

        {/* ── Header ── */}
        <div className={`
          flex items-center justify-between px-5 py-3 flex-shrink-0 border-b
          ${darkMode ? 'border-gray-700/60 bg-gray-900' : 'border-gray-200 bg-white'}
        `}>
          {/* Left: icon + title + badge */}
          <div className="flex items-center gap-3">
            <div className={`
              p-2 rounded-xl
              ${darkMode
                ? 'bg-gradient-to-br from-blue-900 to-purple-900 shadow-inner shadow-blue-700/30'
                : 'bg-gradient-to-br from-blue-100 to-purple-100'
              }
            `}>
              <FiZap className={`w-5 h-5 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`} />
            </div>

            <div>
              <h2 className="text-base font-bold leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">
                  What's New
                </span>
              </h2>
              <p className={`text-xs font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {version}
              </p>
            </div>

            <span className={`
              ml-1 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-widest uppercase
              border animate-pulse
              ${darkMode
                ? 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                : 'bg-blue-100 border-blue-300 text-blue-700'
              }
            `}>
              ⚡ New Release
            </span>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenFullPage}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                transition-all duration-150
                ${darkMode
                  ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200'
                }
              `}
            >
              <FiExternalLink className="w-3.5 h-3.5" />
              View all release notes
            </button>

            <button
              onClick={onDismiss}
              className={`
                p-1.5 rounded-lg transition-all duration-150
                ${darkMode
                  ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }
              `}
              aria-label="Close"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Body: docsService-rendered content ── */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5">
          {docQuery.isLoading && (
            <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Loading release notes…
            </div>
          )}
          {docQuery.isError && (
            <div className="text-sm text-red-400">
              Could not load release notes for {version}.
            </div>
          )}
          {docQuery.data && (
            <DocsMarkdown content={docQuery.data.content} size="medium" />
          )}
        </div>

        {/* ── Footer ── */}
        <div className={`
          flex items-center justify-between px-5 py-3 flex-shrink-0 border-t
          ${darkMode ? 'border-gray-700/60 bg-gray-900/80' : 'border-gray-200 bg-gray-50'}
        `}>
          <p className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            The Phase Phactory · Release notes auto-clear once viewed
          </p>
          <button
            onClick={onDismiss}
            className={`
              px-4 py-2 rounded-lg text-sm font-semibold
              bg-gradient-to-r from-blue-600 to-purple-600
              hover:from-blue-700 hover:to-purple-700
              text-white shadow-md shadow-blue-900/30
              transition-all duration-150 active:scale-95
            `}
          >
            Got it 🎉
          </button>
        </div>
      </div>

      {/* Shimmer keyframe scoped to this modal */}
      <style>{`
        @keyframes whatsNewShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
      `}</style>
    </div>
  );
};
